/* ── BRIEF 05 STEP 5 (via brief 124 §5) — THE DROP LAYER ────────────────────
 *
 * Drop an email anywhere in the app while a matter is open. Mounted ONCE on the
 * app shell.
 *
 * ── ARMED EVERYWHERE (126 §10), AND "DISARMED" IS STILL LITERAL ─────────────
 *
 * [RULED] "drag and drop should work across every screen of the app; the only
 * difference when dropping in a case is the case prefill — otherwise not
 * prefilled, one needs to choose." So `armed` is now `true` on every screen and
 * brief 05's glance check 2 — "on the Dashboard, dragging an email must produce
 * no overlay" — IS DELIBERATELY REVERSED. It tested the fence, and Laurent has
 * moved the fence.
 *
 * The prop and the disarmed path STAY, and not out of caution: `armed` is a
 * real state that fires on every navigation away mid-drag (the `reset` effect
 * below), and "switched off" versus "invisible but still eating events" is a
 * distinction this file has to keep making. Disarmed, `dragover` is NOT
 * prevented and the browser does its ordinary thing.
 *
 * WHAT STILL PROTECTS THE OTHER DRAGS is `looksLikeEmail` / `isOwnRowDrag`
 * below, NOT the arming — and that is now load-bearing rather than a second
 * line. MEASURED across `src/`: the only in-app drag sources are
 * `TaskMatrix.tsx:205` and `dialogs/EisenhowerDialog.tsx:302`, and both set
 * `text/plain` to a BARE TASK ID, which `isOwnRowDrag` rejects. No other file
 * in the tree calls `dataTransfer.setData`.
 *
 * The overlay never blocks clicks when no drag is in progress — it is not
 * rendered at all until a drag enters, and it is `pointer-events: none` for
 * everything except the drop itself.
 *
 * ── THE READER BRANCHES ON WHAT IS PRESENT, NEVER ON THE SOURCE ─────────────
 *
 *   files.length > 0                      CLASSIFY, then POST the file as an
 *                                         email (multipart) or hand it over as
 *                                         a document — see 129 §1 below
 *   maillistrow present                   JSON.parse -> subjects[0], itemIds[0]
 *   text/uri-list starts with "message:"  decodeURIComponent, strip the scheme
 *                                         -> POST { internetMessageId }
 *
 * ── BRIEF 129 §1 — AND "WHAT IS PRESENT" NOW INCLUDES WHAT THE FILE IS ──────
 *
 * 127 §4 asserted that this layer "already sees files and declines them",
 * citing `if (types.includes("Files")) return false;`. That line is inside
 * `isOwnRowDrag`, where `false` means "not one of our own task rows" — it LETS
 * FILES THROUGH — and `looksLikeEmail` accepts `Files` explicitly. So until
 * this brief a dropped Word document was POSTed to `/resolve-drop`, parsed as
 * an `.eml`, and the user met `_dropFailed`.
 *
 * Branch (1) now asks `classifyDroppedFile` FIRST (`lib/dropFileKind.ts`, which
 * carries the discriminator's whole defence) and routes on the answer. The
 * email path below is byte-for-byte what it was; what changed is that it is no
 * longer asked about a document.
 *
 * ── THE OVERLAY STOPS PROMISING "THE EMAIL" ────────────────────────────────
 *
 * A consequence of §1 rather than an addition to it. `dragover` cannot read a
 * file — `dataTransfer.files` is empty until the drop, by design, in every
 * browser — so the overlay cannot know WHICH it is holding. What it can know is
 * the drag's FLAVOUR, which is the same evidence `looksLikeEmail` reads: a
 * `Files` drag may be either, while `maillistrow` and `message:` can only ever
 * be an email. The sentence now says "file" for the first and keeps "email" for
 * the other two, because an overlay that says "Drop the email on this matter"
 * over a PDF is a promise this brief has just made false.
 *
 * The `maillistrow` branch yields A SUBJECT ONLY. Its ids are EWS-format and no
 * translator exists in this tree, so it opens the chooser with `webLink: null`
 * and `autoMinutes: null`, and the chooser disables the time-entry option with
 * a tooltip saying why. THE NUMBER IS NOT FAKED. A later brief adds the
 * translator; the seam is that one field.
 *
 * ── AND IT MUST NOT HIJACK THE EISENHOWER DRAG ─────────────────────────────
 *
 * [MEASURED] `TaskMatrix.tsx` and `EisenhowerDialog.tsx` set `text/plain` to a
 * BARE TASK ID. So a `text/plain` that parses as an integer, with no file and
 * no other flavour present, is one of our own rows being dragged between
 * quadrants — and it is ignored. Glance check 1 is this one, and the brief
 * calls it the check that matters most.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { FS, RAD, Z } from "../lib/tokens";
import { classifyDroppedFile, type DropFileVerdict } from "../lib/dropFileKind";
import type { DropDocumentsFn, SubjectOnlyFn } from "../lib/fnTypes"; // 216 §5 — the callbacks' shapes, by value witness

export type EmailDropDescriptor = {
  internetMessageId: string | null;
  subject: string | null;
  fromAddress: string | null;
  fromName: string | null;
  sentDate: string | null;
  webLink: string | null;
  wordCount: number | null;
  autoMinutes: number | null;
  activityType: string | null;
  description: string | null;
  alreadyBilled: boolean;
};

type Props = {
  /** 126 §10 — `true` on every screen now; the caller still composes it, and
   *  the prop stays because "armed" is a real state (see the header). */
  armed: boolean;
  /** 126 §10 — is a matter open? The ONLY thing this layer knows about one, and
   *  it is used for a SENTENCE and nothing else: the overlay said "Drop the
   *  email on this matter", which is a promise the app can no longer keep on
   *  the Dashboard. The prefill decision is the caller's, not this layer's. */
  matterBound?: boolean;
  /** Resolve a dropped file. Rejects/returns null on failure. 129 §1 — reached
   *  only once the bytes have said the file IS an email. */
  onResolveFile: (file: File) => Promise<EmailDropDescriptor | null>;
  /** 129 §1 — the dropped file is not an email, and the layer says which of the
   *  two ways it knows that: `verdict.kind` is `"document"` when the bytes
   *  identified one, and `"unknown"` when they identified nothing (an empty
   *  file, or an OLE2 container that would not name itself). The caller decides
   *  what each deserves — this layer only reports what arrived. */
  /** 216 §5 [RULED 74] — EVERY FILE OF THE DROP: the documents the bytes identified, together, and the files that
   *  identified nothing (an empty file, an OLE2 container that would not name itself), each with its verdict —
   *  the caller files the first and names the second. The e-mails of the same drop follow, one by one, through
   *  `onResolved`. Called once per drop, before any e-mail is resolved. */
  onDocuments: DropDocumentsFn;
  /** Resolve a dropped `message:` URI. */
  onResolveMessageId: (internetMessageId: string) => Promise<EmailDropDescriptor | null>;
  /** The subject-only branch: no id we can translate, so nothing to resolve. */
  onSubjectOnly: SubjectOnlyFn;
  /** A resolved descriptor — open the chooser. 184 B.4 — the dropped FILE
   *  rides beside it (branch (1) only; the `message:` URI branch has none),
   *  so a task or event born of the drop can file the `.eml` itself. */
  onResolved: (d: EmailDropDescriptor, file?: File) => void;
  /** Resolution failed; say so. */
  onFailed: () => void;
};

/** 216 §5 [RULED 74] — every file of a drop, sorted by what its bytes say: the documents, the files that identified
 *  nothing (each with its verdict), the e-mails. The classification is before any resolve (129 §1's fence). */
async function sortDroppedFiles(files: File[]): Promise<{ documents: File[]; unknown: { file: File; verdict: DropFileVerdict }[]; emails: File[] }> {
  const documents: File[] = [];
  const unknown: { file: File; verdict: DropFileVerdict }[] = [];
  const emails: File[] = [];
  for (const file of files) {
    const verdict = await classifyDroppedFile(file);
    if (verdict.kind === "email") emails.push(file);
    else if (verdict.kind === "document") documents.push(file);
    else unknown.push({ file, verdict });
  }
  return { documents, unknown, emails };
}

/** A `text/plain` that is nothing but an integer is one of OUR dragged rows. */
function isOwnRowDrag(dt: DataTransfer): boolean {
  const types = Array.from(dt.types || []);
  if (types.includes("Files")) return false;
  if (types.some((t) => /maillistrow/i.test(t))) return false;
  if (types.includes("text/uri-list")) return false;
  if (!types.includes("text/plain")) return false;
  const raw = dt.getData("text/plain");
  return /^\d+$/.test((raw || "").trim());
}

/** Does this drag look like something we can take? */
function looksLikeEmail(dt: DataTransfer): boolean {
  const types = Array.from(dt.types || []);
  if (isOwnRowDrag(dt)) return false;
  return (
    types.includes("Files") ||
    types.some((t) => /maillistrow/i.test(t)) ||
    types.includes("text/uri-list")
  );
}

export default function EmailDropLayer({
  armed,
  matterBound = true,
  onResolveFile,
  onDocuments,
  onResolveMessageId,
  onSubjectOnly,
  onResolved,
  onFailed,
}: Props) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  /* 129 §1 — WHICH SENTENCE THE OVERLAY IS ENTITLED TO. Set from the drag's
     flavour on the way in, because that is all a `dragover` may see: a `Files`
     drag could be either kind, `maillistrow` and `message:` can only be an
     email. Never a claim about the file — see the header. */
  const [fileDrag, setFileDrag] = useState(false);
  /* `busy` IS ALSO A REF, and this is not belt-and-braces — it is the fix for
     a defect class this tree has a lint for. The `drop` effect below must know
     whether a resolve is already in flight, but listing `busy` in its
     dependency array while its body calls `setBusy` makes the effect re-enter
     itself; and because it HAS A CLEANUP, the in-flight `await` then resolves
     against a dead closure and NEITHER completion runs. That is the shape that
     shipped a modal which could not draw itself (brief 78), and
     `scripts/lint/effect-sets-its-own-dep.js` refused this file until the
     dependency came out. The state stays, because the OVERLAY renders from it. */
  const busyRef = useRef(false);
  /* dragenter/dragleave fire for every child element the pointer crosses, so a
     boolean would flicker off the moment the cursor entered a nested node. A
     DEPTH COUNTER is the standard remedy and the only one that survives a page
     as deeply nested as a case view. */
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setOver(false);
  }, []);

  /* Disarming must also CLEAR: navigating away mid-drag would otherwise leave
     the overlay painted over a screen that is no longer listening. */
  useEffect(() => {
    if (!armed) reset();
  }, [armed, reset]);

  useEffect(() => {
    if (!armed) return undefined;
    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer || !looksLikeEmail(e.dataTransfer)) return;
      depth.current += 1;
      setFileDrag(Array.from(e.dataTransfer.types || []).includes("Files"));
      setOver(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!e.dataTransfer || !looksLikeEmail(e.dataTransfer)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    };
    const onOver = (e: DragEvent) => {
      /* THE ONE LINE THAT DECIDES WHETHER WE INTERCEPT. Not prevented, the
         browser does its normal thing — which is what "disarmed" has to mean
         for the Eisenhower drag to keep working while a matter is open. */
      if (!e.dataTransfer || !looksLikeEmail(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
    };
  }, [armed]);

  useEffect(() => {
    if (!armed) return undefined;
    const onDrop = async (e: DragEvent) => {
      const dt = e.dataTransfer;
      if (!dt || !looksLikeEmail(dt)) return;
      e.preventDefault();
      reset();
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      try {
        // ── (1) FILES. Laurent's own gesture: desktop Outlook -> Safari —
        //    and, since 129 §1, a document dragged from anywhere else.
        //    216 §5 [RULED 74] — EVERY file the drop holds, not `files[0]`: each
        //    is classified (two `Blob.slice()` reads, no network — the fence
        //    stands: the e-mail resolver is never asked about a document); the
        //    documents and the unclassified go to the caller together, then
        //    each e-mail is resolved in turn.
        const files = dt.files && dt.files.length ? Array.from(dt.files) : [];
        if (files.length) {
          const { documents, unknown, emails } = await sortDroppedFiles(files);
          if (documents.length || unknown.length) onDocuments(documents, unknown);
          for (const file of emails) {
            const d = await onResolveFile(file);
            if (d) onResolved(d, file);
            else onFailed();
          }
          return;
        }
        // ── (2) an OWA list row: subjects[] + EWS itemIds[]. Subject only.
        const rowType = Array.from(dt.types || []).find((x) => /maillistrow/i.test(x));
        if (rowType) {
          try {
            const parsed = JSON.parse(dt.getData(rowType) || "{}");
            const subjects: unknown[] = Array.isArray(parsed.subjects) ? (parsed.subjects as unknown[]) : [];
            const first = subjects.length ? subjects[0] : null;
            const subject = typeof first === "string" ? first : null;
            // 216 §5 [ADVISOR] — only the first row can be read from Outlook on the web; the caller says so
            if (subject) { onSubjectOnly(subject, subjects.length - 1); return; }
          } catch {
            /* fall through to the URI branch */
          }
        }
        // ── (3) a `message:<RFC-id>` URI.
        const uri = (dt.getData("text/uri-list") || "").trim();
        if (/^message:/i.test(uri)) {
          const id = decodeURIComponent(uri.replace(/^message:/i, "")).trim();
          if (id) {
            const d = await onResolveMessageId(id);
            if (d) onResolved(d);
            else onFailed();
            return;
          }
        }
        onFailed();
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    };
    window.addEventListener("drop", onDrop);
    return () => window.removeEventListener("drop", onDrop);
    /* `busy` is deliberately NOT in this array — see `busyRef` above. */
  }, [armed, onResolveFile, onDocuments, onResolveMessageId, onSubjectOnly, onResolved, onFailed, reset]);

  if (!armed || (!over && !busy)) return null;
  return (
    <div
      data-email-drop-overlay={busy ? "busy" : "over"}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z.paneBackdrop,
        /* NEVER intercepts a click. The window-level `drop` listener above is
           what receives the file; this is only paint. */
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--ov, rgba(0,0,0,0.45))",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        style={{
          padding: "18px 28px",
          borderRadius: RAD.r8,
          border: "2px dashed var(--ac)",
          background: "var(--bg)",
          color: "var(--tp)",
          fontSize: FS.s13,
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {/* 129 §1 — FOUR SENTENCES, ONE PER (busy × flavour) PAIR. The
            existing two keep their rows and their meaning for the two drags
            that can only ever be an email; the `Files` pair is new, because
            that drag may be either and the overlay must not choose for it. */}
        {busy
          ? fileDrag
            ? t("emailDrop.reading_file", { fallback: "Reading the file…" })
            : t("emailDrop.resolving", { fallback: "Reading the email…" })
          : matterBound
            ? fileDrag
              ? t("emailDrop.overlay_file", { fallback: "Drop the file on this matter" })
              : t("emailDrop.overlay", { fallback: "Drop the email on this matter" })
            /* 126 §10 — off a matter there is no "this matter" to drop on, and
               a sentence that names one would be the app promising a prefill it
               is about to not make. `emailDrop.overlay` keeps its rows and its
               in-matter consumer (D28). */
            : fileDrag
              ? t("emailDrop.overlay_file_any", { fallback: "Drop the file here" })
              : t("emailDrop.overlay_any", { fallback: "Drop the email here" })}
      </div>
    </div>
  );
}
